import { Archive, PencilSimple, Trash } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useDocuments } from "~/api/queries";
import { DocumentList } from "~/features/documents/DocumentList";
import { Text } from "~/foundation/components/Text";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";

/**
 * Drafts, archive and trash are one query with a different `status` filter — they are
 * document *states*, not separate resources, so they get one component too.
 */
function StatusScene({
  status,
  title,
  blurb,
  emptyMessage,
  icon,
}: {
  status: "draft" | "archived" | "deleted";
  title: string;
  blurb: string;
  emptyMessage: string;
  icon: typeof Archive;
}) {
  const { t } = useTranslation();
  const { data: documents, isLoading } = useDocuments({ status });
  useDocumentTitle(t(title));

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-6">
      <div>
        <Text as="h1" variant="h2">
          {t(title)}
        </Text>
        <Text variant="secondary" className="mt-1">
          {t(blurb)}
        </Text>
      </div>
      <DocumentList
        documents={documents}
        isLoading={isLoading}
        emptyIcon={icon}
        emptyMessage={t(emptyMessage)}
      />
    </div>
  );
}

export function DraftsScene() {
  return (
    <StatusScene
      status="draft"
      icon={PencilSimple}
      title="Drafts"
      blurb="Documents you have started but not published. Only you can see them."
      emptyMessage="You have no drafts."
    />
  );
}

export function ArchiveScene() {
  return (
    <StatusScene
      status="archived"
      icon={Archive}
      title="Archive"
      blurb="Out of the way, still searchable, restorable at any time."
      emptyMessage="Nothing has been archived."
    />
  );
}

export function TrashScene() {
  return (
    <StatusScene
      status="deleted"
      icon={Trash}
      title="Trash"
      blurb="Deleted documents stay here until they are permanently removed."
      emptyMessage="The trash is empty."
    />
  );
}
